import React from "react";
import StatusPill, { type UserStatus } from "../molecules/StatusPill";
import RolePill from "../molecules/RolePill";
import UserTableActions from "../molecules/UserTableActions";
import TablePaginationFooter from "../../ui/molecules/TablePaginationFooter";
import { formatDateTimeWithTime } from "../../../utils/formatUtils";
import type { UserFormData } from "./AddUserDrawer";

export type UsersTableRow = UserFormData & {
  lastLogin: string;
  userId?: string;
  assignmentId?: string;
};

interface UsersTableProps {
  users: UsersTableRow[];
  loading?: boolean;
  error?: string | null;
  page: number;
  totalPages: number;
  pageSize: number;
  totalCount: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  onViewUser?: (user: UserFormData) => void;
  onEditUser?: (user: UserFormData) => void;
  onAssignRole?: (user: UserFormData) => void;
  onToggleStatus?: (user: UsersTableRow, isActive: boolean) => void;
}

const UsersTable: React.FC<UsersTableProps> = ({
  users,
  loading = false,
  error,
  page,
  totalPages,
  pageSize,
  totalCount,
  onPageChange,
  onPageSizeChange,
  onViewUser,
  onEditUser,
  onAssignRole,
  onToggleStatus,
}) => {
  if (loading) {
    return (
      <div className="access-management-table-wrapper">
        <div className="text-center py-5 text-muted">Loading users...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="access-management-table-wrapper">
        <div className="text-center py-5 text-danger">{error}</div>
      </div>
    );
  }

  return (
    <>
      <div className="access-management-table-wrapper">
        <div className="table-responsive">
          <table className="access-management-table table align-middle table-hover mb-0">
            <thead className="table-light">
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Email</th>
                <th scope="col">Contact Number</th>
                <th scope="col">Status</th>
                <th scope="col" className="role-assigned-col">
                  Role Assigned
                </th>
                <th scope="col">Last Login</th>
                <th scope="col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-4 text-muted">
                    No users found
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr key={user.email}>
                <td>
                  <div className="fw-medium">{user.name}</div>
                </td>
                <td>{user.email}</td>
                <td>{user.phone?.trim() ? user.phone : "-"}</td>
                <td>
                  {(() => {
                    const isActive =
                      typeof (user as any).isActive === "boolean"
                        ? (user as any).isActive
                        : (user.status ?? "active") === "active";
                    const derivedStatus: UserStatus = isActive
                      ? "active"
                      : ("inactive" as UserStatus);
                    return <StatusPill status={derivedStatus} />;
                  })()}
                </td>
                    <td className="role-assigned-col">
                      <div className="role-pills-wrapper">
                        {user.roles.map((role) => (
                          <RolePill key={role} role={role} />
                        ))}
                      </div>
                    </td>
                    <td>{formatDateTimeWithTime(user.lastLogin)}</td>
                    <td>
                      <UserTableActions
                        isActive={
                          typeof (user as any).isActive === "boolean"
                            ? (user as any).isActive
                            : user.status === "active"
                        }
                        onView={onViewUser ? () => onViewUser(user) : undefined}
                        onEdit={onEditUser ? () => onEditUser(user) : undefined}
                        onAddUser={onAssignRole ? () => onAssignRole(user) : undefined}
                        onToggleStatus={
                          onToggleStatus
                            ? (checked) => onToggleStatus(user, checked)
                            : undefined
                        }
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      <TablePaginationFooter
        page={page}
        totalPages={Math.max(1, totalPages)}
        pageSize={pageSize}
        totalCount={totalCount}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      />
    </>
  );
};

export default UsersTable;

